import type { PoolModelEnvelopeResult } from '@/api/calculators/types';
import { fmt } from './constants';
import { SummaryTile, KvRow } from './PoolUi';

export function ReverseResultView({ result }: { result: PoolModelEnvelopeResult }) {
  const grid = result.max_pool_volume_grid;
  // Flat heatmap intensity scale.
  const flat = grid.values_m3
    .flat()
    .filter((v): v is number => typeof v === 'number');
  const maxVol = flat.length ? Math.max(...flat, 1) : 1;

  return (
    <div className="space-y-4">
      {/* Configuration brief */}
      <div className="rounded-lg bg-white p-5 shadow-sm">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Capability brief
        </div>
        <div className="mt-1 text-lg font-bold">
          {result.configuration.qty_working} × {result.configuration.model_id}
        </div>
        <div className="mt-1 text-xs text-slate-600">
          {result.design_conditions.climate_name} ·{' '}
          {result.design_conditions.placement} · design{' '}
          {result.design_conditions.design_ambient_c}°C
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <SummaryTile
            label="Total cap (27°C)"
            value={`${fmt(result.configuration.total_capacity_at_27c_kw, 1)} kW`}
          />
          <SummaryTile
            label="At design"
            value={`${fmt(result.configuration.total_capacity_at_design_kw, 1)} kW`}
          />
          <SummaryTile
            label="COP @ design"
            value={fmt(result.configuration.cop_at_design, 2)}
          />
          <SummaryTile
            label="Total power"
            value={`${fmt(result.configuration.total_power_kw, 1)} kW`}
          />
          <SummaryTile
            label="Total flow"
            value={`${fmt(result.configuration.total_flow_m3hr, 1)} m³/h`}
          />
          <SummaryTile
            label="In stock"
            value={result.configuration.in_stock ? 'Yes' : 'Made-to-order'}
          />
        </div>
      </div>

      {/* Max-volume grid */}
      <div
        className="rounded-lg bg-white p-5 shadow-sm"
        data-testid="pool-envelope-grid"
      >
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Max pool volume (m³) · heat-up target × target temperature
        </div>
        <div className="mt-1 text-[11px] text-slate-500">{grid.assumption}</div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[420px] font-mono text-xs">
            <thead>
              <tr className="bg-stone-100 text-slate-600">
                <th className="border border-stone-200 px-2 py-1 text-left">
                  heat-up ↓ / target →
                </th>
                {grid.target_temps_c.map((t) => (
                  <th
                    key={t}
                    className="border border-stone-200 px-2 py-1 text-center"
                  >
                    {t}°C
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.heat_time_targets_h.map((h, hi) => (
                <tr key={h}>
                  <th className="border border-stone-200 bg-stone-100 px-2 py-1 text-left font-semibold text-slate-700">
                    {h} hr
                  </th>
                  {grid.target_temps_c.map((_t, ti) => {
                    const v = grid.values_m3[hi]?.[ti];
                    const display = typeof v === 'number' ? fmt(v, 0) : '—';
                    const intensity =
                      typeof v === 'number' ? Math.min(1, v / maxVol) : 0;
                    return (
                      <td
                        key={ti}
                        className="border border-stone-200 px-2 py-1 text-center font-semibold text-slate-900"
                        style={{
                          background: `rgba(22, 101, 52, ${intensity * 0.3})`,
                        }}
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sweet spot */}
      {result.sweet_spot && (
        <div className="rounded-md border-l-[3px] border-emerald-600 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          <strong>Sweet spot · </strong>
          {result.sweet_spot.summary}
        </div>
      )}

      {/* Operating limits */}
      <div className="rounded-lg bg-white p-5 shadow-sm">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          Operating limits
        </div>
        <dl className="mt-2 grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
          {result.operating_limits.coldest_ambient_for_36h_typical_pool_c !== null && (
            <div>
              <dt className="text-slate-500">
                Coldest ambient (36h, ~
                {result.operating_limits.coldest_ambient_assumed_volume_m3} m³)
              </dt>
              <dd className="font-mono font-bold">
                {result.operating_limits.coldest_ambient_for_36h_typical_pool_c}°C
              </dd>
            </div>
          )}
          <div>
            <dt className="text-slate-500">Max maintenance load</dt>
            <dd className="font-mono font-bold">
              {result.operating_limits.max_maintenance_load_kw} kW
            </dd>
          </div>
        </dl>
        <ul className="mt-3 list-disc pl-5 text-[11px] text-slate-600">
          {result.operating_limits.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>

      {/* Fit check (only when caller passed pool_inputs) */}
      {result.fit && <FitCheckCard fit={result.fit} />}
    </div>
  );
}

export function FitCheckCard({
  fit,
}: {
  fit: NonNullable<PoolModelEnvelopeResult['fit']>;
}) {
  const palette: Record<typeof fit.status, { bg: string; text: string; border: string; label: string }> = {
    undersized: {
      bg: 'bg-rose-50',
      text: 'text-rose-800',
      border: 'border-rose-300',
      label: 'Undersized',
    },
    tight_fit: {
      bg: 'bg-amber-50',
      text: 'text-amber-800',
      border: 'border-amber-300',
      label: 'Tight fit',
    },
    good_fit: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-800',
      border: 'border-emerald-300',
      label: 'Good fit',
    },
    oversized: {
      bg: 'bg-blue-50',
      text: 'text-blue-800',
      border: 'border-blue-300',
      label: 'Oversized',
    },
  };
  const p = palette[fit.status];
  return (
    <div
      data-testid="pool-fit-check"
      className={`rounded-lg border ${p.border} ${p.bg} p-4 ${p.text}`}
    >
      <div className="text-[11px] font-bold uppercase tracking-wider">
        Fit check · {p.label}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <KvRow label="Pool volume" value={`${fit.pool_volume_m3} m³`} />
        <KvRow label="Required" value={`${fit.required_capacity_kw} kW`} />
        <KvRow label="Available" value={`${fit.available_capacity_kw} kW`} />
        <KvRow label="Headroom" value={`${fit.headroom_percent.toFixed(0)}%`} />
      </div>
      <div className="mt-3 text-xs leading-relaxed">{fit.explanation}</div>
    </div>
  );
}

// ─── Small primitives ──────────────────────────────────────────────────────
